import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { baseurl } from "../../config/url";

const safeText = (v) => (v === null || v === undefined || v === "" ? "-" : String(v));

const inr = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n);
};

const dtIN = (d) => {
  if (!d) return "-";
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "-";
  return x.toLocaleString("en-IN");
};

// Image normalizer: supports {data,contentType} base64 OR {url} OR string
const imgSrc = (img) => {
  if (!img) return "";
  if (typeof img === "string") return img;
  if (img?.data && img?.contentType) return `data:${img.contentType};base64,${img.data}`;
  if (img?.url) return img.url;
  if (img?.secure_url) return img.secure_url;
  return "";
};

const pickCategory = (p) => p?.category?.name || p?.category?.title || p?.categoryName || p?.category || "-";
const pickSubcategory = (p) =>
  p?.subcategory?.name || p?.subcategory?.title || p?.subcategoryName || p?.subcategory || "-";

const pickSeller = (p) => p?.seller || p?.sellerId || p?.createdBy || p?.userId || null;
const pickSellerId = (p) => {
  const s = pickSeller(p);
  if (!s) return null;
  if (typeof s === "string") return s;
  return s?._id || s?.id || null;
};
const pickSellerName = (p) => {
  const s = pickSeller(p);
  if (!s) return "-";
  if (typeof s === "string") return "Seller";
  return s?.name || s?.fullName || s?.companyName || s?.shopName || "-";
};
const pickSellerEmail = (p) => {
  const s = pickSeller(p);
  if (!s || typeof s === "string") return "-";
  return s?.email || "-";
};

const Badge = ({ children, tone = "slate" }) => {
  const map = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    green: "bg-green-100 text-green-700 border-green-200",
    red: "bg-red-100 text-red-700 border-red-200",
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    amber: "bg-amber-100 text-amber-800 border-amber-200",
    violet: "bg-violet-100 text-violet-700 border-violet-200",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${map[tone] || map.slate}`}>
      {children}
    </span>
  );
};

const StatCard = ({ label, value }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="mt-1 text-sm font-semibold text-gray-900 break-all">{safeText(value)}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2 border-b border-gray-100 last:border-b-0">
    <div className="sm:w-48 text-sm text-gray-500 font-medium">{label}</div>
    <div className="text-sm text-gray-900 font-semibold break-all">{safeText(value)}</div>
  </div>
);

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isPowerPack, setIsPowerPack] = useState(false);
  const [ppLoading, setPpLoading] = useState(false);

  const isAdmin = (localStorage.getItem("role") || "").toLowerCase() === "admin";

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setLoading(true);

        const { data } = await axios.get(`${baseurl}/api/seller/details/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        });

        // Your API returns product directly (as per your code)
        setProduct(data);
        setIsPowerPack(!!data?.PowerPackVisibility);
      } catch (error) {
        console.error("Error fetching product details:", error);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [id]);

  const images = useMemo(() => {
    const list = Array.isArray(product?.images) ? product.images : [];
    return list
      .map((im) => ({ raw: im, src: imgSrc(im) }))
      .filter((x) => !!x.src);
  }, [product]);

  const togglePowerPack = async () => {
    if (!product?._id) return;

    const updatedValue = !isPowerPack;
    setIsPowerPack(updatedValue);

    try {
      setPpLoading(true);

      await axios.put(
        `${baseurl}/api/admin/update-powerpack/${product._id}`,
        { PowerPackVisibility: updatedValue },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` } }
      );

      alert("PowerPack visibility updated successfully.");
    } catch (err) {
      console.error(err);
      alert("Failed to update PowerPack status.");
      setIsPowerPack(!updatedValue); // revert
    } finally {
      setPpLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
          <div className="text-lg font-semibold text-gray-900">Loading product…</div>
          <div className="text-sm text-gray-500 mt-1">Please wait</div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 max-w-xl w-full">
          <div className="text-lg font-bold text-gray-900">Product not found</div>
          <div className="text-sm text-gray-600 mt-1">
            This product may be deleted or the ID is invalid.
          </div>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const category = pickCategory(product);
  const subcategory = pickSubcategory(product);
  const sellerId = pickSellerId(product);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Top Bar */}
        <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{safeText(product.name)}</h1>
              {product.approved ? <Badge tone="green">Approved</Badge> : <Badge tone="amber">Not Approved</Badge>}
              {product.visibilityPayment ? <Badge tone="violet">Visibility Paid</Badge> : <Badge tone="slate">Visibility Unpaid</Badge>}
              {product.saleType ? <Badge tone="blue">{safeText(product.saleType)}</Badge> : null}
            </div>

            <div className="text-sm text-gray-600 mt-1">
              <span className="font-semibold text-gray-800">{safeText(category)}</span>
              {subcategory !== "-" ? <span> • {safeText(subcategory)}</span> : null}
            </div>

            <div className="text-xs text-gray-500 font-mono mt-1 break-all">ID: {safeText(product._id)}</div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-800"
            >
              Back
            </button>

            <button
              onClick={() => navigate(`/product/${product._id}`)}
              className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-black"
            >
              Open Product Page
            </button>

            {sellerId && (
              <button
                onClick={() => navigate(`/seller-details/${sellerId}`)}
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
              >
                View Seller
              </button>
            )}
          </div>
        </div>

        {/* Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left: Images + quick cards */}
          <div className="lg:col-span-1 space-y-5">
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="p-4 border-b">
                <div className="text-sm font-semibold text-gray-900">Images</div>
                <div className="text-xs text-gray-500 mt-1">Click to view larger</div>
              </div>

              <div className="p-4">
                {images.length > 0 ? (
                  <>
                    <div className="w-full h-64 bg-gray-100 rounded-xl overflow-hidden border">
                      <img
                        src={images[0].src}
                        alt="Main"
                        className="w-full h-full object-cover"
                        onClick={() => window.open(images[0].src, "_blank")}
                      />
                    </div>

                    {images.length > 1 && (
                      <div className="mt-3 grid grid-cols-4 gap-2">
                        {images.slice(1, 9).map((im, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => window.open(im.src, "_blank")}
                            className="h-16 bg-gray-100 rounded-lg overflow-hidden border hover:ring-2 hover:ring-blue-400"
                            title="Open image"
                          >
                            <img src={im.src} alt={`thumb-${idx}`} className="w-full h-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="h-64 bg-gray-50 border border-dashed border-gray-300 rounded-xl flex items-center justify-center text-gray-500 text-sm">
                    No images available
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <StatCard
                label="Price"
                value={`₹${inr(product.price)} ${product.priceUnit ? `(${safeText(product.priceUnit)})` : ""}`}
              />
              <StatCard label="Stock" value={safeText(product.stock)} />
              <StatCard label="Sold" value={safeText(product.sold)} />
              <StatCard label="Visibility Level" value={safeText(product.visibilityLevel)} />
            </div>

            {/* PowerPack */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">PowerPack Visibility</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Current:{" "}
                    <span className={isPowerPack ? "text-green-700 font-semibold" : "text-red-700 font-semibold"}>
                      {isPowerPack ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </div>

                {isAdmin ? (
                  <button
                    onClick={togglePowerPack}
                    disabled={ppLoading}
                    className={`w-14 h-8 flex items-center rounded-full p-1 transition-colors duration-300 ${
                      isPowerPack ? "bg-green-500" : "bg-gray-300"
                    } ${ppLoading ? "opacity-60 cursor-not-allowed" : ""}`}
                    title="Toggle PowerPack"
                  >
                    <div
                      className={`bg-white w-6 h-6 rounded-full shadow-md transform duration-300 ${
                        isPowerPack ? "translate-x-6" : "translate-x-0"
                      }`}
                    />
                  </button>
                ) : (
                  <Badge tone={isPowerPack ? "green" : "red"}>{isPowerPack ? "Yes" : "No"}</Badge>
                )}
              </div>

              {isAdmin && (
                <div className="text-xs text-gray-500 mt-2">
                  Admin can toggle PowerPack. If you get 401, check token/role.
                </div>
              )}
            </div>
          </div>

          {/* Right: Details */}
          <div className="lg:col-span-2 space-y-5">
            {/* Description */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
              <div className="text-sm font-semibold text-gray-900">Description</div>
              <div className="text-sm text-gray-700 mt-2 leading-relaxed">
                {product.description || "No description available."}
              </div>
            </div>

            {/* Core details */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
              <div className="text-sm font-semibold text-gray-900 mb-3">Product Information</div>

              <Row label="Category" value={category} />
              <Row label="Subcategory" value={subcategory} />
              <Row label="Size" value={product.size || "-"} />
              <Row label="Min Quantity" value={product.minquantity} />
              <Row label="Max Quantity" value={product.maxquantity} />
              <Row label="HSN Code" value={product.hsnCode} />
              <Row label="GST Number" value={product.gstNumber} />
              <Row label="Sale Type" value={product.saleType} />
              <Row
                label="Delivery Preference"
                value={
                  product.deliveryPreference
                    ? `${safeText(product.deliveryPreference.duration)} ${safeText(product.deliveryPreference.unit)}`
                    : "-"
                }
              />
              <Row label="Approved" value={product.approved ? "Yes" : "No"} />
              <Row label="Visibility Payment" value={product.visibilityPayment ? "Yes" : "No"} />
              <Row label="Created At" value={dtIN(product.createdAt)} />
              <Row label="Updated At" value={dtIN(product.updatedAt)} />
            </div>

            {/* Seller */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-semibold text-gray-900">Seller</div>
                  <div className="text-sm text-gray-700 mt-2">
                    <span className="font-semibold">{pickSellerName(product)}</span>
                    {pickSellerEmail(product) !== "-" ? (
                      <span className="text-gray-500"> • {pickSellerEmail(product)}</span>
                    ) : null}
                  </div>
                  <div className="text-xs text-gray-500 font-mono mt-1 break-all">
                    Seller ID: {safeText(sellerId)}
                  </div>
                </div>

                {sellerId && (
                  <button
                    onClick={() => navigate(`/seller-details/${sellerId}`)}
                    className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    Open Seller Details
                  </button>
                )}
              </div>
            </div>

            {/* Specifications */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
              <div className="text-sm font-semibold text-gray-900">Specifications</div>

              {Array.isArray(product.specifications) && product.specifications.length > 0 ? (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {product.specifications.map((spec, idx) => (
                    <div key={idx} className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                      <div className="text-xs text-gray-500">Key</div>
                      <div className="text-sm font-semibold text-gray-900 mt-1">{safeText(spec?.key)}</div>
                      <div className="text-xs text-gray-500 mt-2">Value</div>
                      <div className="text-sm font-semibold text-gray-900 mt-1">{safeText(spec?.value)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500 mt-2">No specifications available.</div>
              )}
            </div>
          </div>
        </div>

        <div className="text-xs text-gray-500 mt-6">
          If some fields show “-”, it means your API didn’t send that field for this product.
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;











// import React, { useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import axios from "axios";
// import { baseurl } from "../../config/url";

// const ProductDetails = () => {
//   const { id } = useParams();
//   const [product, setProduct] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [isPowerPack, setIsPowerPack] = useState(false);

//   useEffect(() => {
//     const fetchProductDetails = async () => {
//       try {
//         const { data } = await axios.get(
//           `${baseurl}/api/seller/details/${id}`,
//           {
//             headers: {
//               Authorization: `Bearer ${localStorage.getItem("token")}`,
//             },
//           }
//         );
//         console.log(data); // Debugging: Check product structure
//         setProduct(data);
//         setIsPowerPack(data.PowerPackVisibility || false);
//       } catch (error) {
//         console.error("Error fetching product details:", error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchProductDetails();
//   }, [id]);

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center h-screen bg-gray-100">
//         <p className="text-xl font-semibold text-blue-600 animate-pulse">
//           Loading product details...
//         </p>
//       </div>
//     );
//   }

//   if (!product) {
//     return (
//       <div className="flex items-center justify-center h-screen bg-gray-100">
//         <p className="text-xl font-semibold text-red-600">Product not found.</p>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-white py-10 px-4 lg:px-16">
//       <h1 className="text-4xl font-bold text-gray-800 mb-8">{product.name}</h1>
//       <div className="bg-gray-100 p-6 rounded-lg shadow-lg space-y-4">
//         <p className="text-lg text-gray-700">
//           <strong>Description:</strong> {product.description || "N/A"}
//         </p>
//         <p className="text-lg text-gray-700">
//           <strong>Price:</strong> {product.price} ({product.priceUnit})
//         </p>
//         <p className="text-lg text-gray-700">
//           <strong>Stock:</strong> {product.stock}
//         </p>
//         <p className="text-lg text-gray-700">
//           <strong>Category:</strong>{" "}
//           {product.category?.name || product.category || "N/A"}
//         </p>
//         <p className="text-lg text-gray-700">
//           <strong>Subcategory:</strong>{" "}
//           {product.subcategory?.name || product.subcategory || "N/A"}
//         </p>
//         <p className="text-lg text-gray-700">
//           <strong>Size:</strong> {product.size || "N/A"}
//         </p>
//         <p className="text-lg text-gray-700">
//           <strong>Sold:</strong> {product.sold}
//         </p>
//         <p className="text-lg text-gray-700">
//           <strong>Min Quantity:</strong> {product.minquantity || "N/A"}
//         </p>
//         <p className="text-lg text-gray-700">
//           <strong>Max Quantity:</strong> {product.maxquantity || "N/A"}
//         </p>
//         <p className="text-lg text-gray-700">
//           <strong>HSN Code:</strong> {product.hsnCode || "N/A"}
//         </p>
//         <p className="text-lg text-gray-700">
//           <strong>GST Number:</strong> {product.gstNumber || "N/A"}
//         </p>
//         <p className="text-lg text-gray-700">
//           <strong>Visibility Level:</strong> {product.visibilityLevel || "N/A"}
//         </p>
//         <p className="text-lg text-gray-700">
//           <strong>Delivery Preference:</strong>{" "}
//           {product.deliveryPreference
//             ? `${product.deliveryPreference.duration} ${product.deliveryPreference.unit}`
//             : "N/A"}
//         </p>
//         {/* <p className="text-lg text-gray-700">
//           <strong>PowerPack Visibility:</strong>{" "}
//           {product.PowerPackVisibility ? "Yes" : "No"}
//         </p> */}
//         <div className="text-lg text-gray-700">
//           <strong>PowerPack Visibility:</strong>{" "}
//           <span>{isPowerPack ? "Yes" : "No"}</span>
//           {/* ✅ Admin toggle for PowerPack */}
//           {localStorage.getItem("role") === "admin" && (
//             <div className="flex items-center gap-3 mt-2">
//               <label className="text-sm font-medium text-gray-600">
//                 Toggle PowerPack
//               </label>
//               <button
//                 onClick={async () => {
//                   const updatedValue = !isPowerPack;
//                   setIsPowerPack(updatedValue);
//                   try {
//                     await axios.put(
//                       `${baseurl}/api/admin/update-powerpack/${product._id}`,
//                       { PowerPackVisibility: updatedValue },
//                       {
//                         headers: {
//                           Authorization: `Bearer ${localStorage.getItem(
//                             "token"
//                           )}`,
//                         },
//                       }
//                     );
//                     alert("PowerPack visibility updated successfully.");
//                   } catch (err) {
//                     alert("Failed to update PowerPack status.");
//                     console.error(err);
//                     setIsPowerPack(!updatedValue); // Revert toggle on error
//                   }
//                 }}
//                 className={`w-14 h-8 flex items-center rounded-full p-1 transition-colors duration-300 ${
//                   isPowerPack ? "bg-green-500" : "bg-gray-300"
//                 }`}
//               >
//                 <div
//                   className={`bg-white w-6 h-6 rounded-full shadow-md transform duration-300 ${
//                     isPowerPack ? "translate-x-6" : "translate-x-0"
//                   }`}
//                 ></div>
//               </button>
//             </div>
//           )}
//         </div>

//         <p className="text-lg text-gray-700">
//           <strong>Sale Type:</strong> {product.saleType || "N/A"}
//         </p>
//         <div className="text-lg text-gray-700">
//           <strong>Specifications:</strong>
//           <ul className="list-disc pl-5">
//             {product.specifications?.length > 0
//               ? product.specifications.map((spec, index) => (
//                   <li key={index}>
//                     {spec.key}: {spec.value}
//                   </li>
//                 ))
//               : "N/A"}
//           </ul>
//         </div>
//         <div className="text-lg text-gray-700">
//           <strong>Images:</strong>
//           <ul className="list-disc pl-5">
//             {product.images?.length > 0
//               ? product.images.map((image, index) => (
//                   <li key={index}>{image.$oid}</li>
//                 ))
//               : "N/A"}
//           </ul>
//         </div>
//         <p className="text-lg text-gray-700">
//           <strong>Seller:</strong>{" "}
//           {product.seller
//             ? `${product.seller.name} (${product.seller.email})`
//             : "N/A"}
//         </p>

//         <p className="text-lg text-gray-700">
//           <strong>Approved:</strong> {product.approved ? "Yes" : "No"}
//         </p>
//         <p className="text-lg text-gray-700">
//           <strong>Visibility Payment:</strong>{" "}
//           {product.visibilityPayment ? "Yes" : "No"}
//         </p>

//         <p className="text-lg">
//   <strong className="text-gray-700">PowerPack Visibility:</strong>{" "}
//   <span className={product.PowerPackVisibility ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
//     {product.PowerPackVisibility ? "Yes" : "No"}
//   </span>
// </p>
//         <div className="text-lg text-gray-700">
//           <strong>Images:</strong>
//           <div className="grid grid-cols-2 gap-4">
//             {product.images?.length > 0 ? (
//               product.images.map((image, index) => (
//                 <div key={index} className="w-full max-w-sm">
//                   {image.data ? (
//                     <img
//                       src={`data:${image.contentType};base64,${image.data}`}
//                       alt={image.filename || `Image ${index + 1}`}
//                       className="w-full rounded-lg shadow-md"
//                     />
//                   ) : (
//                     <p className="text-gray-500">Image data not available</p>
//                   )}
//                 </div>
//               ))
//             ) : (
//               <p className="text-gray-500">No images available</p>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default ProductDetails;
